import api from './api';
import { config } from '../config/config';

export interface SearchResult {
  id: string;
  type: 'driver' | 'vehicle' | 'order' | 'location' | 'owner' | 'customer';
  title: string;
  subtitle?: string;
  url: string;
  data?: any; // Raw data from API
}

export interface ProSearchData {
  pronumber: number;
  /** 'Y' when the order/template is soft-deleted — drives the red row highlight. */
  deleted?: string | null;
  /**
   * True when the order is CLOSED: present in ordersall but no longer in
   * orderscurrent. Drives the amber row tint / card treatment. Custom Filter and
   * Custom Search are permanently all-scope, so closed orders are always in these
   * results — this is what makes them identifiable. Red (deleted) outranks amber.
   */
  isClosed?: boolean;
  masterPro?: number | null;
  moveType?: string | null;
  commodity?: string | null;
  csr?: string | null;
  csrName?: string | null;
  whseCsr?: string | null;
  whseCsrName?: string | null;
  dispatcher?: string | null;
  dispatcherName?: string | null;
  terminal?: string | null;
  container?: string | null;
  chassisId?: string | null;
  size?: string | null;
  equipType?: string | null;
  ssline?: string | null;
  vessel?: string | null;
  voyage?: string | null;
  pickupNumber?: string | null;
  po?: string | null;
  bol?: string | null;
  billToName?: string | null;
  customerName?: string | null;
  isDrop?: string | null;
  comments?: string | null;
  lfd?: string | null;
  available?: string | null;
  outGate?: string | null;
  perDiem?: string | null;
  docCut?: string | null;
  cutoff?: string | null;
  puAppt?: string | null;
  pickupFrom?: string | null;
  deliverTo?: string | null;
  deliverCity?: string | null;
  deliverState?: string | null;
  deliverZip?: string | null;
  delivAppt?: string | null;
  delivApptTime1?: string | null;
  delivApptTime2?: string | null;
  deliveryDate?: string | null;
  deliveryDriver?: string | null;
  returnTo?: string | null;
  terminateDate?: string | null;
  terminateDriver?: string | null;
  traceTerminate?: string | null;
  billFrom?: string | null;
  invDate?: string | null;
  billToRef?: string | null;
  serviceFailureDate?: string | null;
  serviceFailureReason?: string | null;
  serviceFailureSubReason?: string | null;
  serviceFailureDriver?: string | null;
  serviceFailureCount?: number | null;
}

export interface GlobalSearchResult {
  value: string;
  label: string;
  description?: string;
  group: string;
  type: 'pro' | 'template' | 'driver' | 'customer' | 'dispatcher' | 'driver_orders' | 'movetype' | 'container' | 'location' | 'chassis' | 'bol' | 'quote';
  data?: any;
  /**
   * Fan-out index set by the backend when a row-expanding additional field
   * (invoice line item) duplicates a pro into one row per line item. Undefined
   * for normal single rows. Used to keep every fanned row through the batch
   * merge below instead of collapsing them to one (they share the pronumber).
   */
  _dupeIndex?: number;
}

/**
 * The rows, plus the exact size of the PRO group when the per-category `limit`
 * truncated it (#33651).
 *
 * The property rides ON the array — the same shape `String.prototype.match`
 * uses — so every existing caller keeps treating the return value as the list it
 * always was, and only the one consumer that needs the total reaches for it.
 * `proTotal` is undefined whenever nothing was truncated, in which case the rows
 * ARE the whole set and counting them is exact.
 */
export type GlobalSearchResults = GlobalSearchResult[] & { proTotal?: number };

class SearchService {
  /**
   * Perform a unified global search using the backend endpoint
   */
  async unifiedGlobalSearch(
    query: string,
    limit: number = 5,
    signal?: AbortSignal,
    scope: 'current' | 'all' = 'current',
    types?: string,
    includeDeleted: boolean = false,
    additionalFields?: string[],
    /** Return container/chassis matches as full PRO rows in the "PRO Numbers"
     *  group instead of thin identifier rows. The results page sets this so a
     *  container carried by several orders renders in the PRO grid; the
     *  typeahead leaves it off to keep its identifier-labeled dropdown rows. */
    ordersAsPros: boolean = false
  ): Promise<GlobalSearchResults> {
    if (!query || query.trim().length < 2) {
      return [];
    }

    try {
      const params: Record<string, string | number | boolean> = { q: query, limit, scope };
      if (types) params.types = types;
      if (includeDeleted) params.include_deleted = true;
      if (additionalFields && additionalFields.length) params.additional_fields = additionalFields.join(',');
      if (ordersAsPros) params.orders_as_pros = true;
      const response = await api.get('/search/global', { params, signal });
      const results: GlobalSearchResults = response.data.data || [];
      // Absent (or null) whenever the backend didn't have to truncate.
      if (typeof response.data.proTotal === 'number') {
        results.proTotal = response.data.proTotal;
      }
      return results;
    } catch (error: any) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') throw error;
      console.error('Unified global search failed:', error);
      return [];
    }
  }

  /**
   * Batch search for multiple values (PROs, containers, drivers).
   *
   * A pasted list of any size must come back in full. Rather than cram every
   * term into one long GET query-string (which risks tripping URL-length / WAF
   * limits, and hit the backend's per-request cap), the list is split into
   * fixed-size chunks, each fetched as its own request (bounded concurrency),
   * then merged. Order rows (pro/template) are deduped by value across chunks,
   * mirroring the backend's _dedup_pro_results; every other type passes through.
   * A small paste stays a single request — behavior is unchanged there.
   */
  async batchSearch(terms: string[], signal?: AbortSignal, additionalFields?: string[]): Promise<GlobalSearchResult[]> {
    if (!terms.length) return [];

    const CHUNK_SIZE = 150;   // keeps each request's query-string well under URL/WAF limits
    const MAX_CONCURRENCY = 6;

    const chunks: string[][] = [];
    for (let i = 0; i < terms.length; i += CHUNK_SIZE) {
      chunks.push(terms.slice(i, i + CHUNK_SIZE));
    }

    const extraFieldsParam = additionalFields && additionalFields.length ? additionalFields.join(',') : undefined;

    const fetchChunk = async (chunk: string[]): Promise<GlobalSearchResult[]> => {
      try {
        const params: Record<string, string> = { terms: chunk.join(',') };
        if (extraFieldsParam) params.additional_fields = extraFieldsParam;
        const response = await api.get('/search/batch', {
          params,
          signal,
        });
        return response.data.data || [];
      } catch (error: any) {
        if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') throw error;
        console.error('Batch search failed:', error);
        return [];
      }
    };

    // Run chunks through a small worker pool, keeping results in chunk order.
    const perChunk: GlobalSearchResult[][] = new Array(chunks.length);
    let next = 0;
    const worker = async () => {
      while (next < chunks.length) {
        const idx = next++;
        perChunk[idx] = await fetchChunk(chunks[idx]);
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(MAX_CONCURRENCY, chunks.length) }, worker),
    );

    // Merge in chunk order, collapsing repeated order rows (same pro/template).
    // When a row-expanding additional field (invoice line item) fans a pro into
    // several rows, each carries a distinct `_dupeIndex`, so the key keeps them
    // all — only genuine same-order double-matches (no _dupeIndex) collapse.
    const merged: GlobalSearchResult[] = [];
    const seen = new Set<string>();
    for (const list of perChunk) {
      for (const r of list || []) {
        if (r.type === 'pro' || r.type === 'template') {
          const key = `${r.type}:${r.value}:${r._dupeIndex ?? ''}`;
          if (seen.has(key)) continue;
          seen.add(key);
        }
        merged.push(r);
      }
    }
    return merged;
  }

  /**
   * Re-read ONE order's rich PRO rows — the same payload shape the results grids
   * already hold, so a caller can drop them straight onto the rows it refreshes.
   *
   * Used after a single-PRO trace from a grid: the trace writes LFD / Available /
   * Out Gate / Trace Date / … on the order, which leaves the on-screen row showing
   * pre-trace values until something re-reads it. Overlaying the trace endpoint's
   * own `updated_order_fields` is NOT equivalent — those are keyed and formatted
   * for the /pro screen (`MM/DD/YYYY`), while these grids render SQL-formatted
   * dates that drop the year in the current year (`%m/%d`). Mixing the two would
   * put one differently-formatted cell in an otherwise uniform column and break
   * that column's sorting and filtering, which run on those strings.
   *
   * Rides on /search/batch rather than a dedicated endpoint: it is already an
   * exact-PRO lookup over ordersall/legsall (so closed and deleted orders resolve),
   * already returns `_build_pro_data` + the same `entity_enrichment` extras the LC
   * card endpoint attaches, and is already registered and granted to everyone who
   * can reach these grids — a new endpoint would need its own api_endpoints row
   * plus per-group grants before it answered anything but 403.
   *
   * `additionalFields` MUST be the host's current extra-column selection. Trace
   * Date is the column a user most naturally adds for this feature, and it is an
   * added column, not part of the base payload — omitting the selection here left
   * exactly the field the trace just wrote showing its pre-trace value.
   *
   * Returns a LIST because a row-expanding pick (invoice line items) fans one pro
   * into one row per line item, which is how the grids hold it too. Rows come back
   * in the backend's order, so callers pair them up with their own rows for that
   * pro positionally.
   */
  async refreshProRows(
    pronumber: number | string,
    additionalFields?: string[],
  ): Promise<ProSearchData[]> {
    const term = String(pronumber).trim();
    if (!term) return [];
    try {
      const rows = await this.batchSearch([term], undefined, additionalFields);
      return rows
        .filter((r) => (r.type === 'pro' || r.type === 'template') && String(r.value) === term)
        .map((r) => r.data as ProSearchData)
        .filter(Boolean);
    } catch (error) {
      console.error('PRO row refresh failed:', error);
      return [];
    }
  }

  /**
   * Custom Search — match a pasted list of values against ONE chosen field.
   *
   * The V1 "Custom Search" window (lib_search.pl dosavecustomsearch). Distinct
   * from batchSearch, which probes every facet at once: here the caller picks the
   * field, so a container that looks like a PRO number can't drag in stray orders.
   *
   * POSTs a JSON array instead of a comma-joined query string — LOT NUMBER values
   * may contain commas and spaces, so joining them would corrupt the search, and a
   * long paste would risk URL/WAF length limits. That also means no chunking.
   */
  async customSearch(
    searchBy: string,
    terms: string[],
    includeDeleted: boolean,
    signal?: AbortSignal,
    additionalFields?: string[],
  ): Promise<GlobalSearchResult[]> {
    if (!terms.length) return [];
    try {
      const response = await api.post(
        '/search/custom',
        {
          search_by: searchBy,
          terms,
          include_deleted: includeDeleted,
          ...(additionalFields?.length ? { additional_fields: additionalFields } : {}),
        },
        { signal },
      );
      return response.data.data || [];
    } catch (error: any) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') throw error;
      console.error('Custom search failed:', error);
      return [];
    }
  }

  /**
   * Custom Filter — apply field/operator/value conditions to orders.
   *
   * `conditions` are the shared FilterConditionRow rows; the backend resolves them
   * through DynamicQueryBuilder and returns rows in the same rich-PRO shape as
   * customSearch/batchSearch, so the results grid renders them identically.
   *
   * UNCAPPED: this is the reporting surface people export from, so the backend
   * returns every match and the request does not grow with the result set. Expect a
   * broad filter to take a while and to come back large — there is no ceiling to
   * report, which is why this returns a plain array again.
   *
   * THROWS on failure rather than returning []. The other search methods swallow
   * errors, which is survivable when a request either works or doesn't — but this one
   * has a real failure the user must be told about: exceeding the server's time budget
   * returns 400 with wording that names the fix. Swallowed, that renders as "no results
   * found", i.e. "your filter matched nothing" — the opposite of what happened.
   */
  async customFilterSearch(
    conditions: Array<{ field: string; compare: string; values: string }>,
    includeDeleted: boolean,
    signal?: AbortSignal,
    additionalFields?: string[],
  ): Promise<GlobalSearchResult[]> {
    if (!conditions.length) return [];
    try {
      const response = await api.post(
        '/search/custom-filter',
        {
          conditions,
          include_deleted: includeDeleted,
          ...(additionalFields?.length ? { additional_fields: additionalFields } : {}),
        },
        { signal },
      );
      return response.data.data || [];
    } catch (error: any) {
      console.error('Custom filter search failed:', error);
      throw error;
    }
  }

  /** The caller's saved order Custom Filters (scope='orders'). */
  async listOrderCustomFilters(): Promise<Array<{ recno: number; name: string; filter: string }>> {
    try {
      const response = await api.get('/search/custom-filters');
      return response.data || [];
    } catch (error) {
      console.error('Failed to list order custom filters:', error);
      return [];
    }
  }

  /** Upsert a named order Custom Filter by name. */
  async saveOrderCustomFilter(name: string, filter: string): Promise<void> {
    await api.post('/search/custom-filters', { name, filter });
  }

  /**
   * Perform a global search across multiple endpoints (legacy)
   */
  async globalSearch(query: string): Promise<SearchResult[]> {
    if (!query || query.trim().length < 2) {
      return [];
    }

    const results: SearchResult[] = [];

    try {
      // Execute all searches in parallel for better performance
      const [
        driverResults,
        vehicleResults,
        orderResults,
        ownerResults
      ] = await Promise.allSettled([
        this.searchDrivers(query),
        this.searchVehicles(query),
        this.searchOrders(query),
        this.searchOwners(query)
      ]);

      // Combine results
      if (driverResults.status === 'fulfilled') {
        results.push(...driverResults.value);
      }
      if (vehicleResults.status === 'fulfilled') {
        results.push(...vehicleResults.value);
      }
      if (orderResults.status === 'fulfilled') {
        results.push(...orderResults.value);
      }
      if (ownerResults.status === 'fulfilled') {
        results.push(...ownerResults.value);
      }

      return results;
    } catch (error) {
      return [];
    }
  }

  /**
   * Search drivers from Samsara
   */
  private async searchDrivers(query: string): Promise<SearchResult[]> {
    try {
      const response = await api.get('/samsara/drivers');
      const drivers = response.data;

      // Filter drivers by name or ID
      const filtered = drivers.filter((driver: any) => {
        const name = driver.name?.toLowerCase() || '';
        const id = driver.id?.toLowerCase() || '';
        const externalIds = driver.externalIds
          ? Object.values(driver.externalIds).join(' ').toLowerCase()
          : '';

        const searchTerm = query.toLowerCase();
        return name.includes(searchTerm) ||
               id.includes(searchTerm) ||
               externalIds.includes(searchTerm);
      });

      return filtered.slice(0, 5).map((driver: any) => ({
        id: `driver-${driver.id}`,
        type: 'driver' as const,
        title: driver.name || 'Unknown Driver',
        subtitle: `Driver ID: ${driver.externalIds?.driverCode || driver.id || 'N/A'}`,
        url: '/safety/drivers',
        data: driver
      }));
    } catch (error) {
      return [];
    }
  }

  /**
   * Search vehicles from Samsara
   */
  private async searchVehicles(query: string): Promise<SearchResult[]> {
    try {
      const response = await api.get('/samsara/vehicles');
      const vehicles = response.data;

      // Filter vehicles by name, license plate, or VIN
      const filtered = vehicles.filter((vehicle: any) => {
        const name = vehicle.name?.toLowerCase() || '';
        const licensePlate = vehicle.licensePlate?.toLowerCase() || '';
        const vin = vehicle.vin?.toLowerCase() || '';
        const id = vehicle.id?.toLowerCase() || '';

        const searchTerm = query.toLowerCase();
        return name.includes(searchTerm) ||
               licensePlate.includes(searchTerm) ||
               vin.includes(searchTerm) ||
               id.includes(searchTerm);
      });

      return filtered.slice(0, 5).map((vehicle: any) => ({
        id: `vehicle-${vehicle.id}`,
        type: 'vehicle' as const,
        title: vehicle.name || 'Unknown Vehicle',
        subtitle: vehicle.licensePlate
          ? `License: ${vehicle.licensePlate}`
          : vehicle.vin
            ? `VIN: ${vehicle.vin}`
            : `ID: ${vehicle.id}`,
        url: '/fleet-management/live-map',
        data: vehicle
      }));
    } catch (error) {
      return [];
    }
  }

  /**
   * Search orders using the dedicated search endpoint
   */
  private async searchOrders(query: string): Promise<SearchResult[]> {
    // Store legacy CMS URL for use in render functions
    const legacyCmsUrl = config.LEGACY_CMS_URL;
    try {
      // Use the new dedicated search endpoint for Pro Numbers
      const response = await api.get('/orders/search', {
        params: {
          q: query,
          limit: 5
        }
      });

      const orders = response.data.data || [];

      return orders.map((order: any) => ({
        id: `order-${order.pronumber}`,
        type: 'order' as const,
        title: `Pro #${order.pronumber}${order.moveType ? ` (${order.moveType})` : ''}`,
        subtitle: [
          order.customerName,
          order.container
        ].filter(Boolean).join(' - '),
        url: `${legacyCmsUrl}/index_cms.pl?action=savecustomsearch&contlist=${order.pronumber}&searchtype=pronumber`,
        data: order
      }));
    } catch (error) {
      return [];
    }
  }

  /**
   * Search owners
   */
  private async searchOwners(query: string): Promise<SearchResult[]> {
    try {
      const response = await api.get('/owners');
      const owners = response.data;

      // Filter owners by name or ID
      const filtered = owners.filter((owner: any) => {
        const name = owner.name?.toLowerCase() || '';
        const id = owner.id?.toString().toLowerCase() || '';
        const code = owner.owner_code?.toLowerCase() || '';

        const searchTerm = query.toLowerCase();
        return name.includes(searchTerm) ||
               id.includes(searchTerm) ||
               code.includes(searchTerm);
      });

      return filtered.slice(0, 5).map((owner: any) => ({
        id: `owner-${owner.id}`,
        type: 'owner' as const,
        title: owner.name || 'Unknown Owner',
        subtitle: owner.owner_code ? `Code: ${owner.owner_code}` : `ID: ${owner.id}`,
        url: '/safety/owners',
        data: owner
      }));
    } catch (error) {
      return [];
    }
  }
}

export default new SearchService();
